/* eslint-disable no-unused-expressions */
import { Link } from "gatsby";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Flex } from "rebass/styled-components";

import { layout } from "../constants/constants";
import GlobalNavDesktop from "../navigation/global-nav-desktop/global-nav-desktop";
import GlobalNavMobile from "../navigation/global-nav-mobile/global-nav-mobile";
import ShIcon from "../shared/components/sh-icon/sh-icon";
import { Media } from "../shared/contexts/media/MediaContext";
import { UiStateContext } from "../shared/contexts/ui-state/UiStateContext";
import { DeviceType } from "../shared/hooks/use-breakpoint/device-type";
import useBreakpoint from "../shared/hooks/use-breakpoint/useBreakpoint";
import useFeatureToggle from "../shared/hooks/use-feature-toggle/useFeatureToggle";
import { FeatureToggle, GlobalNavContent, IconPack, PrimaryHeaderContent } from "../shared/models/app-data-model";
import "./primary-header.scss";

export type PrimaryHeaderProps = {
  headerContent: PrimaryHeaderContent;
  navContent: GlobalNavContent;
  title: string;
  featureToggles: FeatureToggle[];
  iconPack: IconPack[];
  campaignPageActive?: boolean;
  visible?: boolean;
  dynamicPrimaryHeader?: boolean;
  subsitePresent?: boolean;
  lightFontMode?: boolean;
};

const PrimaryHeader: React.FC<PrimaryHeaderProps> = ({
  campaignPageActive,
  featureToggles,
  headerContent,
  iconPack,
  lightFontMode,
  navContent,
  dynamicPrimaryHeader,
  subsitePresent,
  title,
  visible
}: PrimaryHeaderProps) => {
  const uiStateContext = useContext(UiStateContext);
  const breakpoint = useBreakpoint();
  const [isInitiallyDesktopView, setIsInitiallyDesktopView] = useState(false);
  const [headerClassName, setHeaderClassName] = useState(`primary-header ${dynamicPrimaryHeader && !subsitePresent ? "transparent" : ""}`);
  const showSearch = useFeatureToggle("search", featureToggles);
  const globalSearchPageEnalbed = useFeatureToggle("global-search-page", featureToggles);
  const auxNavEnabled = useFeatureToggle("aux-nav", featureToggles);
  const showMyChartLink = useFeatureToggle("mychart-link", featureToggles);

  const handleMenuClick = useCallback((): void => {
    uiStateContext.setAuxNavOpen?.(false);
    uiStateContext.setMenuOpen?.(!uiStateContext.menuOpen);
    uiStateContext.setBackButtonNavItem?.(false);

    // if the subsite nav was open then we should close it when the aux nav is opened.
    uiStateContext.setSubsiteMenuOpen?.(false);
    uiStateContext.setSubsiteMenuOpenIndex?.(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uiStateContext.menuOpen,
    uiStateContext.setAuxNavOpen,
    uiStateContext.setBackButtonNavItem,
    uiStateContext.setMenuOpen,
    uiStateContext.setSubsiteMenuOpen,
    uiStateContext.setSubsiteMenuOpenIndex
  ]);

  const handleAuxNavClick = useCallback((): void => {
    uiStateContext.setMenuOpen?.(!uiStateContext.menuOpen);
    uiStateContext.setAuxNavOpen?.(!uiStateContext.auxNavOpen);
    uiStateContext.setBackButtonNavItem?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uiStateContext.auxNavOpen,
    uiStateContext.setAuxNavOpen,
    uiStateContext.setBackButtonNavItem,
    uiStateContext.setMenuOpen,
    uiStateContext.menuOpen
  ]);

  const handleSearchClick = useCallback((): void => {
    uiStateContext.setSearchOpen?.(true);
    uiStateContext.setSubsiteMenuOpen?.(false);
    uiStateContext.setSubsiteMenuOpenIndex?.(0);
    uiStateContext.setBackButtonNavItem?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uiStateContext.setSearchOpen,
    uiStateContext.setSubsiteMenuOpen,
    uiStateContext.setSubsiteMenuOpenIndex,
    uiStateContext.setBackButtonNavItem
  ]);

  /**
   * This effect checks if we're initially on a desktop size on first render or not
   * This effect fixes an edge case bug when switching viewport sizes at runtime
   */
  useEffect(() => {
    setIsInitiallyDesktopView(breakpoint >= DeviceType.Desktop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This effect fixes an edge case bug when switching viewport sizes at runtime
   */
  useEffect(() => {
    // We close the secondary menu if the user switches from a mobile or desktop viewport to the other
    // This prevents the secondary menu from potentially being in a broken empty state
    if (isInitiallyDesktopView && breakpoint < DeviceType.Desktop) {
      uiStateContext.setMenuOpen?.(false);
      uiStateContext.setSubsiteMenuOpen?.(false);
      if (breakpoint) {
        setIsInitiallyDesktopView(breakpoint >= DeviceType.Desktop);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiStateContext.menuOpen, uiStateContext.setMenuOpen, uiStateContext.setSubsiteMenuOpen, isInitiallyDesktopView, breakpoint]);

  /**
   * This hook controls the complex styles for our primary header depending on various states
   * it supports the header when there is not a subsite, when there is a subsite, and when a user has scrolled,
   * and changes the style class name accordingly.
   */
  useEffect(() => {
    if (dynamicPrimaryHeader && (uiStateContext.pageHasScrolled || uiStateContext.auxNavOpen)) {
      // if we're showing the dynamicPrimaryHeader (High Impact Hero) but the user has started scrolling down the page, we need to fade-in the header from the transparency used for the hero:
      setHeaderClassName("fade-in primary-header");
    } else if (dynamicPrimaryHeader && !subsitePresent) {
      // Add check for subsite - if there is a subsite we dont want to show this as transparent
      // if we're showing the dynamicPrimaryHeader (High Impact Hero), we need to add the transparency used for the hero:
      setHeaderClassName("transparent primary-header");
    } else if (breakpoint >= DeviceType.Desktop && !visible && !campaignPageActive) {
      // if we're on desktop and the header is not supposed to be visible, we need to add `slide-up` to slide the header up
      setHeaderClassName("primary-header slide-up");
    } else {
      setHeaderClassName("primary-header");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakpoint, uiStateContext.pageHasScrolled, uiStateContext.auxNavOpen, visible, campaignPageActive]);

  // the style classes for the header container
  const containerClasses =
    dynamicPrimaryHeader || !subsitePresent || breakpoint < DeviceType.Desktop ? `transparent header-container` : `header-container`;

  return (
    <div className={containerClasses} id="header-container">
      <Flex className={headerClassName} data-testid={visible ? "header-visible" : "header-hidden"}>
        <Flex className="primary-header-content" width={layout.pageWidth} px={layout.pagePaddingX}>
          <div className="left-content">
            {!uiStateContext?.backButtonNavItem || breakpoint >= DeviceType.Desktop ? (
              headerContent.logo?.url && (
                <Link to={"/"} className="logo-area">
                  <img
                    src={
                      (lightFontMode && !uiStateContext.menuOpen) ||
                      (lightFontMode && uiStateContext.setAuxNavOpen && breakpoint >= DeviceType.Desktop)
                        ? headerContent?.light_text_logo?.url
                          ? headerContent?.light_text_logo?.url
                          : headerContent.logo.url
                        : headerContent.logo.url
                    }
                    className={`logo`}
                    alt={`${title} Logo`}
                  ></img>
                </Link>
              )
            ) : (
              <button className="back-button unstyled-btn" onClick={() => uiStateContext?.setBackButtonNavItem?.(false)}>
                <ShIcon iconPack={iconPack} className="back-button-icon" iconName="caret_left" />
                <span className="back-button-text">Back</span>
              </button>
            )}
            {!campaignPageActive && (
              <GlobalNavDesktop lightFontMode={lightFontMode} iconPack={iconPack} content={navContent} subsiteNavPresent={subsitePresent} />
            )}
          </div>
          {!campaignPageActive && (
            <>
              <Media greaterThanOrEqual="desktop" className="right-content">
                {showMyChartLink && (
                  <a
                    title="MyChart Login"
                    className={`primary-nav-item my-chart-link ${
                      lightFontMode && !uiStateContext.menuOpen && !subsitePresent ? "filter-white" : ""
                    }`}
                    href="https://mychart.spectrumhealth.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    MyChart Login
                  </a>
                )}
                {showSearch && !globalSearchPageEnalbed && (
                  <button
                    className="search-icon-wrapper unstyled-btn"
                    data-testid="search-icon"
                    onClick={handleSearchClick}
                    aria-label="Search"
                  >
                    <ShIcon
                      iconPack={iconPack}
                      iconName="search"
                      className={
                        uiStateContext?.searchOpen
                          ? "icon"
                          : `icon search-icon ${lightFontMode && !uiStateContext.menuOpen && !subsitePresent ? "filter-white" : ""}`
                      }
                    />
                  </button>
                )}
                {showSearch && globalSearchPageEnalbed && (
                  <Link
                    to={"/search"}
                    className="global-nav-btn global-nav-search-btn unstyled-btn"
                    aria-label="Search"
                    data-testid="search-icon"
                  >
                    <ShIcon iconPack={iconPack} iconName="search" className="icon search-icon" />
                  </Link>
                )}
                {auxNavEnabled && (
                  <button className="unstyled-btn" tabIndex={0} data-testid="menu-icon" onClick={() => handleAuxNavClick()}>
                    <ShIcon
                      className="menu-icon cursor-pointer"
                      iconPack={iconPack}
                      iconName={uiStateContext?.auxNavOpen ? "close" : "menu"}
                    />
                  </button>
                )}
              </Media>
              <Media lessThan="desktop" className="right-content">
                {showSearch && (
                  <button className="search-icon-wrapper unstyled-btn" onClick={handleSearchClick} aria-label="Search">
                    <ShIcon
                      iconPack={iconPack}
                      iconName="search"
                      className={
                        uiStateContext?.searchOpen
                          ? "icon"
                          : `icon search-icon ${lightFontMode && !uiStateContext.menuOpen && !subsitePresent ? "filter-white" : ""}`
                      }
                    />
                  </button>
                )}
                <button
                  tabIndex={0}
                  onClick={() => handleMenuClick()}
                  data-testid="mobile-menu-icon"
                  className="mobile-menu-icon unstyled-btn"
                >
                  <ShIcon
                    iconPack={iconPack}
                    className={`menu-icon ${lightFontMode && !uiStateContext.menuOpen && !subsitePresent ? "filter-white" : ""}`}
                    iconName={uiStateContext?.menuOpen ? "close" : "menu"}
                  />
                </button>
              </Media>
            </>
          )}
        </Flex>
      </Flex>
      {uiStateContext?.menuOpen && !campaignPageActive && (
        <GlobalNavMobile iconPack={iconPack} content={navContent} featureToggles={featureToggles} />
      )}
    </div>
  );
};

export default PrimaryHeader;
